import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Avatar,
  Divider,
  CardActions,
  CardHeader,
  Stack,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as FormIcon,
  Update as UpdateIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  MoreVert as MoreIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import FormBuilder from "./FormBuilder";
import moment from "moment";
import dataProvider from "../components/dataProvider";

const PAGE_SIZE = 12;

const FormCardSkeleton = () => (
  <Grid item xs={12} sm={6} lg={4}>
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      }}
    >
      <CardHeader
        avatar={<Avatar sx={{ bgcolor: "#eee" }} />}
        title={<Box sx={{ bgcolor: "#eee", height: 24, width: "60%", borderRadius: 1, mb: 1 }} />}
        subheader={<Box sx={{ bgcolor: "#eee", height: 16, width: "40%", borderRadius: 1 }} />}
      />
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ bgcolor: "#eee", height: 16, width: "80%", borderRadius: 1, mb: 1 }} />
        <Box sx={{ bgcolor: "#eee", height: 16, width: "60%", borderRadius: 1, mb: 1 }} />
        <Box sx={{ bgcolor: "#eee", height: 16, width: "70%", borderRadius: 1, mb: 1 }} />
        <Box sx={{ bgcolor: "#eee", height: 16, width: "50%", borderRadius: 1, mb: 1 }} />
      </CardContent>
      <CardActions sx={{ p: 2, pt: 0 }}>
        <Box display="flex" gap={1} width="100%">
          <Box sx={{ bgcolor: "#eee", height: 32, width: "40%", borderRadius: 2 }} />
          <Box sx={{ bgcolor: "#eee", height: 32, width: "40%", borderRadius: 2 }} />
        </Box>
      </CardActions>
    </Card>
  </Grid>
);

const FormsList = ({ customerId }) => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, formId: null });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Función para obtener formularios del backend usando dataProvider
  const fetchForms = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await dataProvider("GET_LIST", "enterprise/forms", {
        filter: customerId ? { customerId } : {},
        pagination: { page, perPage: PAGE_SIZE },
        sort: { field: "updatedAt", order: "DESC" }, // Ordenar por fecha de actualización descendente
      });
      setForms(res.data || []);
      setTotalPages(res.totalPages || 1);
    } catch (err) {
      setError(err.message || "Error al cargar formularios");
    } finally {
      setLoading(false);
    }
  };

  // Función para eliminar formulario
  const handleDeleteForm = async (formId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/enterprise/forms/${formId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Error al eliminar formulario");
      }

      fetchForms();
      setDeleteDialog({ open: false, formId: null });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateForm = () => {
    setEditingForm(null);
    setShowFormBuilder(true);
  };

  const handleEditForm = (form) => {
    setEditingForm(form);
    setShowFormBuilder(true);
  };

  const handleCloseFormBuilder = () => {
    setShowFormBuilder(false);
    setEditingForm(null);
    fetchForms();
  };

  const getFormStatus = (form) => {
    if (form.deletedAt) return { status: "deleted", label: "Eliminado", color: "error" };
    if (form.status === false) return { status: "inactive", label: "Inactivo", color: "warning" };
    return { status: "active", label: "Activo", color: "success" };
  };

  const getFormStats = (form) => {
    const totalPages = form.metadata?.totalPages || 0;
    const totalComponents = form.metadata?.totalComponents || 0;
    const customerCount = form.customers?.length || 0;

    return { totalPages, totalComponents, customerCount };
  };

  useEffect(() => {
    fetchForms();
    // eslint-disable-next-line
  }, [customerId, page]);

  if (showFormBuilder) {
    return <FormBuilder onClose={handleCloseFormBuilder} editingForm={editingForm} />;
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, minHeight: "100vh" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
          sx={{
            backgroundColor: "white",
            p: 2.5,
            borderRadius: 2,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Box>
            <Typography variant="h5" component="h1" fontWeight="bold" gutterBottom>
              Plantillas
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestiona y crea plantillas personalizadas para tus clientes
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateForm}
            sx={{
              px: 2.5,
              py: 1.2,
              borderRadius: 2,
              textTransform: "none",
              color: "white",
              fontSize: "0.9rem",
              fontWeight: 600,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              "&:hover": {
                boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
              },
            }}
          >
            Crear Plantilla
          </Button>
        </Box>
        <Grid container spacing={3}>
          {Array.from({ length: PAGE_SIZE }).map((_, i) => (
            <FormCardSkeleton key={i} />
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, minHeight: "100vh" }}>
      {/* Header mejorado */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}
        sx={{
          backgroundColor: "white",
          p: 2.5,
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold" gutterBottom>
            Plantillas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gestiona y crea plantillas personalizadas para tus clientes
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateForm}
          sx={{
            px: 2.5,
            py: 1.2,
            borderRadius: 2,
            textTransform: "none",
            color: "white",
            fontSize: "0.9rem",
            fontWeight: 600,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            "&:hover": {
              boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
            },
          }}
        >
          Crear Plantilla
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)} variant="filled">
          {error}
        </Alert>
      )}

      {/* Lista de formularios */}
      {forms.length === 0 ? (
        <Card
          sx={{
            textAlign: "center",
            py: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            borderRadius: 3,
          }}
        >
          <CardContent>
            <FormIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
            <Typography variant="h5" color="text.secondary" gutterBottom fontWeight="600">
              No hay plantillas creadas
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3} sx={{ maxWidth: 400, mx: "auto" }}>
              Crea tu primer plantilla personalizada para empezar a recopilar información de tus clientes de manera
              profesional
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateForm}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              Crear Primera Plantilla
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <Grid container spacing={3}>
            {forms.map((form) => {
              const formStatus = getFormStatus(form);
              const stats = getFormStats(form);

              return (
                <Grid item xs={12} sm={6} lg={4} key={form._id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      borderRadius: 3,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    {/* Header de la card */}
                    <CardHeader
                      avatar={
                        <Avatar
                          sx={{
                            bgcolor: "#ff770d",
                          }}
                        >
                          <FormIcon />
                        </Avatar>
                      }
                      action={
                        <Box>
                          <Tooltip title="Más opciones">
                            <IconButton size="small">
                              <MoreIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                      title={
                        <Typography variant="h6" component="h2" noWrap fontWeight="600">
                          {form.name}
                        </Typography>
                      }
                      subheader={
                        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                          <Chip
                            label={formStatus.label}
                            color={formStatus.color}
                            size="small"
                            icon={formStatus.status === "active" ? <ActiveIcon /> : <InactiveIcon />}
                          />
                        </Box>
                      }
                    />

                    <CardContent sx={{ flexGrow: 1, pt: 0 }}>
                      {/* Estadísticas del formulario */}
                      <Stack spacing={2} mb={3}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" alignItems="center" gap={1}>
                            <SettingsIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {stats.totalPages} página{stats.totalPages !== 1 ? "s" : ""}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <FormIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {stats.totalComponents} componente{stats.totalComponents !== 1 ? "s" : ""}
                            </Typography>
                          </Box>
                        </Box>

                        {stats.customerCount > 0 && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              Asociado a {stats.customerCount} cliente{stats.customerCount !== 1 ? "s" : ""}
                            </Typography>
                          </Box>
                        )}

                        <Divider />

                        {/* Fechas */}
                        <Box>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <CalendarIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              Creado: {moment(form.createdAt).format("DD/MM/YYYY")}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <UpdateIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              Actualizado: {moment(form.updatedAt).format("DD/MM/YYYY")}
                            </Typography>
                          </Box>
                        </Box>
                      </Stack>
                    </CardContent>

                    {/* Acciones */}
                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Box display="flex" gap={1} width="100%">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleEditForm(form)}
                          sx={{
                            flex: 1,
                            textTransform: "none",
                            borderRadius: 2,
                          }}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={() => setDeleteDialog({ open: true, formId: form._id })}
                          sx={{
                            textTransform: "none",
                            borderRadius: 2,
                          }}
                        >
                          Eliminar
                        </Button>
                      </Box>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          {/* Controles de paginación */}
          <Box display="flex" justifyContent="center" alignItems="center" mt={4} gap={2}>
            <Button
              variant="outlined"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Anterior
            </Button>
            <Typography variant="body2" color="text.secondary">
              Página {page} de {totalPages}
            </Typography>
            <Button
              variant="outlined"
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              Siguiente
            </Button>
          </Box>
        </>
      )}
      {/* Dialog de confirmación de eliminación */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, formId: null })}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="600">
            Confirmar eliminación
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary">
            ¿Estás seguro de que quieres eliminar esta plantilla? Esta acción no se puede deshacer y se perderán todos
            los datos asociados.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={() => setDeleteDialog({ open: false, formId: null })}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => handleDeleteForm(deleteDialog.formId)}
            color="error"
            variant="contained"
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormsList;
